import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import toast from "react-hot-toast";
import { serializeValue, cellText } from "../utils/firestoreValueUtils";
import { detectFieldType } from "./explorerFilterUtils";
import { searchBackups, restoreBackupRecord } from "../services/backupService";

function copyToClipboard(text, label) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Copy failed")
  );
}

export default function RowDetailDrawer({
  open,
  row,
  collectionName,
  canEdit,
  canDelete,
  onClose,
  onEdit,
  onDelete,
  onRestored,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) {
  const [tab, setTab] = useState(0);
  const [backup, setBackup] = useState(null);
  const [checkingBackup, setCheckingBackup] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const data = row?.data || {};
  const serialized = useMemo(() => serializeValue(data), [data]);
  const jsonText = useMemo(
    () => JSON.stringify({ id: row?.id, ...serialized }, null, 2),
    [row?.id, serialized]
  );

  useEffect(() => {
    let cancelled = false;
    setBackup(null);
    if (!open || !row?.id || !collectionName) return undefined;
    setCheckingBackup(true);
    searchBackups({ collection: collectionName, documentId: row.id, max: 1 })
      .then((records) => {
        if (!cancelled) setBackup(records[0] || null);
      })
      .catch(() => {
        if (!cancelled) setBackup(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingBackup(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row?.id, collectionName]);

  const handleRestore = async () => {
    if (!backup) return;
    try {
      await restoreBackupRecord(backup);
      toast.success("Document restored from backup");
      onRestored?.();
    } catch (err) {
      toast.error(`Restore failed: ${err.message}`);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: "100%", sm: 480 } } } }}>
      {row && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
              Document Details
            </Typography>
            <Tooltip title="Previous">
              <span>
                <IconButton size="small" onClick={onPrev} disabled={!hasPrev}>
                  <NavigateBeforeIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next">
              <span>
                <IconButton size="small" onClick={onNext} disabled={!hasNext}>
                  <NavigateNextIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ px: 2, pb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={collectionName} color="primary" variant="outlined" />
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {row.id}
              </Typography>
              <Tooltip title="Copy Document ID">
                <IconButton size="small" onClick={() => copyToClipboard(row.id, "Document ID")}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
            <Tab label="Formatted" />
            <Tab label="JSON" />
            <Tab label="Raw" />
          </Tabs>
          <Divider />

          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {tab === 0 && (
              <Stack divider={<Divider flexItem />} spacing={1}>
                {Object.keys(data).length === 0 && (
                  <Typography variant="body2" color="text.secondary">No fields.</Typography>
                )}
                {Object.entries(data).map(([key, value]) => (
                  <Box key={key}>
                    <Typography variant="caption" color="text.secondary">{key}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{cellText(value)}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
            {tab === 1 && (
              <Box component="pre" sx={{ m: 0, fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {jsonText}
              </Box>
            )}
            {tab === 2 && (
              <Stack spacing={1}>
                {Object.entries(data).map(([key, value]) => (
                  <Stack key={key} direction="row" spacing={1} alignItems="baseline">
                    <Chip size="small" label={detectFieldType(value)} variant="outlined" />
                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 90 }}>{key}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-word", flex: 1 }}>
                      {JSON.stringify(serializeValue(value))}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />
          <Stack direction="row" spacing={1} sx={{ p: 2 }} flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(jsonText, "JSON")}>
              Copy JSON
            </Button>
            {canEdit && (
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(row)}>
                Edit
              </Button>
            )}
            <Tooltip title={backup ? "Restore latest backup" : checkingBackup ? "Checking backups…" : "No backup found"}>
              <span>
                <Button size="small" color="warning" startIcon={<RestoreIcon />} onClick={handleRestore} disabled={!backup}>
                  Restore
                </Button>
              </span>
            </Tooltip>
            {canDelete && (
              <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete <strong>{row?.id}</strong> from <strong>{collectionName}</strong>. A backup is created
            first so it can be restored from the Restore Center.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmDelete(false);
              onDelete?.(row);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
